/**
 * 廠商對帳單(REQ-20260831001)—— 應付側的請款文件,版面鏡射月結對帳單
 * (`StatementView`),同一個 `.print-area` + `Cmd+P`,**沒有引進 PDF 套件**
 * (沿用 REQ-20260806002 §3.4 的取捨)。
 *
 * 與客戶側的三處差別,都來自資料模型而不是版面偏好:
 *  - 抬頭右欄是**付款條件 / 付款天數 / 結帳日**(廠商主檔有這三個欄位),
 *    不是客戶側的結帳週期與請款到期日。
 *  - 金額欄的說法換成**已付 / 未付 / 應付**。
 *  - **沒有折讓單區塊** —— `sales_order_credit_notes` 只有應收側有,
 *    進貨側的折讓只有進貨單單頭的 `allowanceAmount` 一個來源。
 *
 * 幾個小工具(`Row` / `formatQty`)與 `StatementView` 各留一份,
 * 理由同 `GroupedCustomerStatements` 裡的同名函式:抽成共用檔要動到另外兩個
 * 已經在跑的版面,不在本次範圍內。
 */
import type { StatementLine, SupplierStatement } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { useCompanyProfile } from '../accounting/api';

export function SupplierStatementView({
  statement,
}: {
  statement: SupplierStatement;
}): JSX.Element {
  const s = statement;
  // 沒設定過(或這支查失敗)時各欄位印「—」—— 不擋住列印
  const { data: company } = useCompanyProfile();
  return (
    <div className="print-area bg-white shadow-sm rounded-md p-6 text-sm">
      <header className="border-b pb-3">
        <h2 className="text-lg font-semibold text-slate-900">廠商對帳單</h2>
        <div className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Row
            label="廠商"
            value={`${s.supplierFullName}${s.supplierCode ? `（${s.supplierCode}）` : ''}`}
          />
          <Row label="帳款區間" value={`${s.periodStart} ~ ${s.periodEnd}`} />
          <Row label="統一編號" value={s.taxId ?? '—'} />
          <Row label="付款條件" value={s.paymentTerms ?? '未設定'} />
          <Row label="地址" value={s.address ?? '—'} />
          <Row label="付款天數" value={s.paymentDays == null ? '—' : `${s.paymentDays} 天`} />
          <Row label="聯絡人 / 電話" value={[s.contact, s.phone].filter(Boolean).join(' / ') || '—'} />
          <Row label="結帳日" value={s.closingDay == null ? '—' : `${s.closingDay} 日`} />
        </div>
        {/* 我方公司(REQ-20260817004 的同一支設定),與廠商那格分開一個區塊 */}
        <div
          className="mt-2 grid gap-x-6 gap-y-1 border-t pt-2 sm:grid-cols-2"
          data-testid="supplier-statement-company"
        >
          <Row label="本公司" value={company?.name || '—'} />
          <Row label="本公司電話" value={company?.phone || '—'} />
          <Row label="本公司地址" value={company?.address || '—'} />
          <Row label="本公司傳真" value={company?.fax || '—'} />
        </div>
      </header>

      <section className="mt-4">
        <h3 className="font-medium text-slate-700">本期進貨明細</h3>
        {s.lines.length === 0 ? (
          <p className="mt-1 text-slate-500">本期沒有進貨單。</p>
        ) : (
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">交易日期</th>
                <th className="px-2 py-1.5">交易單號</th>
                <th className="px-2 py-1.5">貨品編號</th>
                <th className="px-2 py-1.5">品名</th>
                <th className="px-2 py-1.5 text-right">數量</th>
                <th className="px-2 py-1.5">單位</th>
                <th className="px-2 py-1.5 text-right">單價</th>
                <th className="px-2 py-1.5 text-right">金額</th>
                <th className="px-2 py-1.5">備註說明</th>
                {/* 折讓 / 已付 / 未付是**單頭**金額,只印在該單第一列(同日期 / 單號) */}
                <th className="px-2 py-1.5 text-right">折讓金額</th>
                <th className="px-2 py-1.5 text-right">已付金額</th>
                <th className="px-2 py-1.5 text-right">未付金額</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.map((l) =>
                printLines(l).map((p, i) => (
                  <tr key={p.key} className="border-b last:border-0">
                    {/* 同一張單第二個品項以後,日期與單號留白(比照舊系統的請款明細) */}
                    <td className="px-2 py-1.5 whitespace-nowrap">{i === 0 ? l.txnDate : ''}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                      {i === 0 ? l.orderNo : ''}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono">{p.productCode}</td>
                    <td className="px-2 py-1.5">
                      {p.placeholder ? (
                        <span className="text-slate-500">（無品項明細）</span>
                      ) : (
                        <>
                          {p.productName}
                          {p.productSpec ? (
                            <span className="text-slate-500">（{p.productSpec}）</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{p.qty}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{p.unitName}</td>
                    {/* 單價照 DECIMAL 原樣印:四捨五入會讓數量 × 單價 看起來對不上金額 */}
                    <td className="px-2 py-1.5 text-right font-mono">{p.unitPrice}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMoney(p.amount)}</td>
                    <td className="px-2 py-1.5">{p.notes}</td>
                    {/* 折讓沒填 = 0(印「—」會讓人以為這欄壞掉) */}
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.allowanceAmount ?? 0) : ''}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.settledAmount) : ''}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {i === 0 ? formatMoney(l.outstanding) : ''}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </section>

      {s.payments.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-medium text-slate-700">本期付款</h3>
          <table className="mt-2 w-full">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="px-2 py-1.5">日期</th>
                <th className="px-2 py-1.5">付款單號</th>
                <th className="px-2 py-1.5 text-right">金額</th>
              </tr>
            </thead>
            <tbody>
              {s.payments.map((p) => (
                <tr key={p.settlementId} className="border-b last:border-0">
                  <td className="px-2 py-1.5 whitespace-nowrap">{p.settledAt}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">{p.documentNo}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {formatMoney(p.totalSettled)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {/*
        滾動式合計,與應收側同一條算式換一側:
        期初 + 本期進貨(含稅)- 折讓 - 付款 = 期末。本期進貨拆成未稅 + 營業稅顯示
        (兩者相加就是本期合計,沒有多加一次)。
      */}
      <section className="mt-4 border-t pt-3 sm:w-96 sm:ml-auto" data-testid="statement-totals">
        <dl className="grid grid-cols-2 gap-y-1">
          <dt className="text-slate-500">數量合計</dt>
          <dd className="text-right font-mono">{formatQty(totalQty(s))}</dd>
          <dt className="text-slate-500">前期累計未付帳款</dt>
          <dd className="text-right font-mono">{formatMoney(s.openingBalance)}</dd>
          <dt className="text-slate-500">累計預付貨款</dt>
          <dd className="text-right font-mono">{formatMoney(s.openingPrepaid)}</dd>
          <dt className="text-slate-500">本期未稅金額</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodSubtotal)}</dd>
          <dt className="text-slate-500">營業稅</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodTax)}</dd>
          <dt className="text-slate-500">本期合計</dt>
          <dd className="text-right font-mono">{formatMoney(s.periodPurchases)}</dd>
          <dt className="text-slate-500">折讓金額</dt>
          <dd className="text-right font-mono">-{formatMoney(s.periodAllowance)}</dd>
          <dt className="text-slate-500">已付金額</dt>
          <dd className="text-right font-mono">-{formatMoney(s.periodPayments)}</dd>
          <dt className="font-medium text-slate-900 border-t pt-1">本期累計應付</dt>
          <dd className="text-right font-mono font-semibold border-t pt-1">
            {formatMoney(s.closingBalance)}
          </dd>
        </dl>
        {/*
          預付貨款目前只有廠商主檔的期初值,系統沒有逐期追蹤動用 —— 標示清楚,
          免得被當成會跟著期間變動的餘額(理由同 REQ-20260815013 §四 的預收貨款)。
        */}
        <p className="mt-1 text-xs text-slate-400">
          累計預付貨款為廠商主檔的期初設定值,不含期間內的動用與新增。
        </p>
        {/*
          明細的「已付金額」是逐單累計的沖帳金額,合計的「已付金額」只算本期間內的
          付款 —— 兩個數字對不起來是正常的,不講清楚會被當成 bug。
        */}
        <p className="mt-1 text-xs text-slate-400">
          明細的「已付金額」為該單截至目前的累計沖帳金額,與合計的「已付金額」
          (僅本期間內的付款)口徑不同。
        </p>
      </section>
    </div>
  );
}

/** 表身的一列 —— 品項、單頭差額、或「這張單沒有品項」的替身 */
interface PrintLine {
  key: string;
  /** true = 沒有品項的歷史單,品名印「（無品項明細）」 */
  placeholder: boolean;
  productCode: string;
  productName: string;
  productSpec: string | null;
  qty: string;
  unitName: string;
  unitPrice: string;
  amount: string;
  notes: string;
}

/**
 * 一張單 → 要印的列。結構同 `StatementView` 的同名函式,只有補差額那一列的
 * **標籤不同**:進貨單**沒有** `plateFee` / `dieFee` 欄位
 * (`calcPurchaseTotals()` 的 subtotal 就是品項加總),所以這裡印「版費 / 刀模費」
 * 會是一句不成立的話。
 *
 * 那為什麼還要補這一列:歷史 mdb 匯入的進貨單,單頭 `subtotal` 直接取自舊系統的
 * 「合計金額」、品項另外從明細表匯入,兩者對不起來是真的會發生的資料形狀
 * (`mdb-import.service.ts`)。差額不印出來,使用者拿品項加總對帳就會少一筆。
 */
function printLines(line: StatementLine): PrintLine[] {
  // 歷史 mdb 匯入單可能只有單頭、沒有品項 —— 仍要印一列,
  // 否則單據金額進了底部合計、明細裡卻找不到對應的一行
  if (line.items.length === 0) {
    return [
      {
        key: `order-${line.orderId}`,
        placeholder: true,
        productCode: '',
        productName: '',
        productSpec: null,
        qty: '',
        unitName: '',
        unitPrice: '',
        amount: line.subtotal,
        notes: '',
      },
    ];
  }

  const lines: PrintLine[] = line.items.map((item) => ({
    key: `line-${item.lineId}`,
    placeholder: false,
    productCode: item.productCode,
    productName: item.productName,
    productSpec: item.productSpec,
    qty: item.qty,
    unitName: item.unitName ?? '',
    unitPrice: item.unitPrice,
    amount: item.amount,
    notes: item.notes ?? '',
  }));

  // 先四捨五入到分,否則浮點誤差會讓每張單都長出一列 0 元的差額
  const diff =
    Math.round(
      (Number(line.subtotal) - line.items.reduce((sum, i) => sum + Number(i.amount), 0)) * 100,
    ) / 100;
  if (diff !== 0) {
    lines.push({
      key: `diff-${line.orderId}`,
      placeholder: false,
      productCode: '',
      productName: '單頭金額差額',
      productSpec: null,
      qty: '',
      unitName: '',
      unitPrice: '',
      amount: String(diff),
      notes: '',
    });
  }
  return lines;
}

/** 本期所有品項的數量合計(單位可能不同,與舊系統的請款明細一樣只是逐列加總) */
function totalQty(s: SupplierStatement): number {
  return s.lines.reduce(
    (sum, l) => l.items.reduce((lineSum, item) => lineSum + Number(item.qty), sum),
    0,
  );
}

/** 數量是 DECIMAL(14,4),不能套 `formatMoney`(那支會四捨五入成整數) */
function formatQty(n: number): string {
  return n.toLocaleString('zh-TW', { maximumFractionDigits: 4 });
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}
